import AppComponent from '../base/app-component.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import { getCasseMap } from '../lib/casse-info.ts';
import { getSindacatiMap } from '../lib/sindacati-info.ts';
import { withCessatoInfo } from '../lib/cessato-info.ts';

interface LavoratoreRicerca {
  id: number;
  cognome: string;
  nome: string;
  codiceFiscale: string | null;
  posizioni: Record<string, unknown>;
}

/**
 * wc-nuova-disdetta — Inserimento nuova disdetta sindacale.
 *
 * Form a 2 step caricato in iframe (modale Bootstrap):
 *   Step 1 → Ricerca lavoratore (debounce 300ms, min 3 char)
 *   Step 2 → Form disdetta (cassa, tipo, sigla, data, note) + verifica warning
 *
 * Successo: postMessage al parent (iframe) per chiusura modale.
 */
export class NuovaDisdetta extends AppComponent {
  static override componentName = 'wc-nuova-disdetta';

  private lavoratoreSelezionato: LavoratoreRicerca | null = null;
  private warningsVisibili = false;
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;

  protected override get templateId() { return 'nuova-disdetta'; }

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      casse: Object.values(getCasseMap()),
      sindacati: Object.values(getSindacatiMap()),
    };
  }

  protected override afterRender(): void {
    this.setupRicerca();
    this.setupCambiaLavoratore();
    this.setupForm();
    this.setupTipoToggle();
  }

  // --- Step 1: Ricerca lavoratore ---

  private setupRicerca(): void {
    const input = this.$('[data-search-input]') as HTMLInputElement | null;
    if (!input) return;

    this.listen(input, 'input', () => {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      const q = input.value.trim();
      if (q.length < 3) {
        const results = this.$('[data-search-results]');
        if (results) results.innerHTML = '';
        return;
      }
      this.searchTimeout = setTimeout(() => this.doSearch(q), 300);
    });
  }

  private async doSearch(q: string): Promise<void> {
    const container = this.$('[data-search-results]');
    if (!container) return;

    templateParseToElement('shared-loading-state', container, {});

    const result = await this.api.lavoratori.cerca(q, 20);
    if (!result.ok) {
      templateParseToElement('shared-error-state', container, { message: result.error });
      return;
    }

    const lavoratori = result.data.risultati;
    templateParseToElement('shared-lavoratori-risultati', container, {
      lavoratori: withCessatoInfo(lavoratori),
      casse: Object.values(getCasseMap()),
      action: 'seleziona-lavoratore',
    });

    this.delegate.offAll('click', '[data-action="seleziona-lavoratore"]');
    this.delegate.on('click', '[data-action="seleziona-lavoratore"]', (e: Event) => {
      const row = (e.target as HTMLElement).closest('[data-action="seleziona-lavoratore"]') as HTMLElement;
      const id = Number(row.dataset.id);
      const lav = lavoratori.find(l => l.id === id);
      if (lav) {
        this.lavoratoreSelezionato = lav;
        this.mostraForm();
      }
    });
  }

  // --- Step 2: Form disdetta ---

  private mostraForm(): void {
    const stepRicerca = this.$('[data-step="ricerca"]');
    const stepForm = this.$('[data-step="form"]');
    if (stepRicerca) stepRicerca.style.display = 'none';
    if (stepForm) stepForm.style.display = '';

    const info = this.$('[data-lavoratore-info]');
    if (info && this.lavoratoreSelezionato) {
      const l = this.lavoratoreSelezionato;
      info.innerHTML = `<strong>${l.cognome} ${l.nome}</strong>` +
        (l.codiceFiscale ? ` &mdash; <code>${l.codiceFiscale}</code>` : '');
    }

    // Auto-set cassa se territorio monocassa
    const casse = Object.values(getCasseMap());
    if (casse.length === 1) {
      const cassaSelect = this.$('[data-field="cassa"]') as HTMLSelectElement | null;
      if (cassaSelect) cassaSelect.value = casse[0]!.codice;
    }

    this.resetWarnings();
  }

  private setupCambiaLavoratore(): void {
    this.delegate.on('click', '[data-action="cambia-lavoratore"]', () => {
      this.lavoratoreSelezionato = null;
      this.resetWarnings();

      const stepRicerca = this.$('[data-step="ricerca"]');
      const stepForm = this.$('[data-step="form"]');
      if (stepRicerca) stepRicerca.style.display = '';
      if (stepForm) stepForm.style.display = 'none';

      const input = this.$('[data-search-input]') as HTMLInputElement | null;
      if (input) { input.value = ''; input.focus(); }
      const results = this.$('[data-search-results]');
      if (results) results.innerHTML = '';
    });
  }

  private setupForm(): void {
    const form = this.$('[data-action="salva"]') as HTMLFormElement | null;
    if (!form) return;

    this.listen(form, 'submit', (e: Event) => {
      e.preventDefault();
      if (this.warningsVisibili) return;
      this.verificaESalva();
    });

    this.delegate.on('click', '[data-action="salva-comunque"]', () => this.salva());
    this.delegate.on('click', '[data-action="modifica"]', () => this.resetWarnings());
  }

  private setupTipoToggle(): void {
    const tipoSelect = this.$('[data-field="tipo"]') as HTMLSelectElement | null;
    if (!tipoSelect) return;

    this.listen(tipoSelect, 'change', () => {
      const isGenerico = tipoSelect.value === 'GENERICO';
      const gruppoSigla = this.$('[data-gruppo-sigla]') as HTMLElement | null;
      const siglaSelect = this.$('[data-field="sigla"]') as HTMLSelectElement | null;

      if (gruppoSigla) gruppoSigla.style.display = isGenerico ? 'none' : '';
      if (siglaSelect) {
        siglaSelect.required = !isGenerico;
        if (isGenerico) siglaSelect.value = '';
      }
    });
  }

  // --- Verifica e salvataggio ---

  private async verificaESalva(): Promise<void> {
    if (!this.lavoratoreSelezionato) return;

    const dati = this.raccogliDati();
    if (!dati) return;

    // GENERICO non ha bisogno di verifica
    if (dati.tipo === 'GENERICO') {
      await this.salva();
      return;
    }

    const submitBtn = this.$('[data-submit-btn]') as HTMLButtonElement | null;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Verifica...';
    }

    const result = await this.api.disdette.verifica({
      lavoratoreId: this.lavoratoreSelezionato.id,
      cassa: dati.cassa,
      tipo: dati.tipo as 'INVIATA' | 'RICEVUTA' | 'GENERICO',
      sigla: dati.sigla,
    });

    if (!result.ok) {
      this.mostraErrore(result.error);
      this.resetSubmitBtn();
      return;
    }

    if (result.data.warnings.length > 0) {
      this.mostraWarnings(result.data.warnings);
      this.resetSubmitBtn();
      return;
    }

    await this.salva();
  }

  private async salva(): Promise<void> {
    if (!this.lavoratoreSelezionato) return;

    const dati = this.raccogliDati();
    if (!dati) return;

    const submitBtn = this.$('[data-submit-btn]') as HTMLButtonElement | null;
    const salvaComunqueBtn = this.$('[data-action="salva-comunque"]') as HTMLButtonElement | null;
    const btnAttivo = salvaComunqueBtn && this.warningsVisibili ? salvaComunqueBtn : submitBtn;

    if (btnAttivo) {
      btnAttivo.disabled = true;
      btnAttivo.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Salvataggio...';
    }

    const result = await this.api.disdette.crea({
      lavoratoreId: this.lavoratoreSelezionato.id,
      cassa: dati.cassa,
      tipo: dati.tipo as 'INVIATA' | 'RICEVUTA' | 'GENERICO',
      sigla: dati.sigla || undefined,
      dataDisdetta: dati.dataDisdetta,
      note: dati.note || undefined,
    });

    if (!result.ok) {
      this.mostraErrore(result.error);
      if (btnAttivo) {
        btnAttivo.disabled = false;
        btnAttivo.innerHTML = '<i class="bi bi-check-circle me-1"></i>Salva comunque';
      }
      this.resetSubmitBtn();
      return;
    }

    Weext.toast('Disdetta inserita con successo', { type: 'success' });

    const detail = { type: 'disdetta-creata', id: result.data.id };
    setTimeout(() => this.notifyAndClose(detail), 800);
  }

  // --- Raccolta dati form ---

  private raccogliDati(): { cassa: string; tipo: string; sigla: string; dataDisdetta: string; note: string } | null {
    const cassa = (this.$('[data-field="cassa"]') as HTMLSelectElement | null)?.value || '';
    const tipo = (this.$('[data-field="tipo"]') as HTMLSelectElement | null)?.value || '';
    const sigla = (this.$('[data-field="sigla"]') as HTMLSelectElement | null)?.value || '';
    const dataDisdetta = (this.$('[data-field="dataDisdetta"]') as HTMLInputElement | null)?.value || '';
    const note = (this.$('[data-field="note"]') as HTMLTextAreaElement | null)?.value || '';

    const isGenerico = tipo === 'GENERICO';
    if (!cassa || !tipo || !dataDisdetta) return null;
    if (!isGenerico && !sigla) return null;
    return { cassa, tipo, sigla: isGenerico ? '' : sigla, dataDisdetta, note };
  }

  // --- Warning ---

  private mostraWarnings(warnings: string[]): void {
    this.warningsVisibili = true;
    const container = this.$('[data-warnings]');
    if (!container) return;

    container.style.display = '';
    container.innerHTML =
      '<div class="alert alert-warning py-2">' +
      '<strong><i class="bi bi-exclamation-triangle me-1"></i>Attenzione</strong>' +
      '<ul class="mb-2 mt-1">' +
      warnings.map(w => `<li>${w}</li>`).join('') +
      '</ul>' +
      '<div class="d-flex gap-2">' +
      '<button class="btn btn-warning btn-sm" data-action="salva-comunque">' +
      '<i class="bi bi-check-circle me-1"></i>Salva comunque</button>' +
      '<button class="btn btn-outline-secondary btn-sm" data-action="modifica">' +
      '<i class="bi bi-pencil me-1"></i>Modifica</button>' +
      '</div>' +
      '</div>';
  }

  private resetWarnings(): void {
    this.warningsVisibili = false;
    const container = this.$('[data-warnings]');
    if (container) {
      container.style.display = 'none';
      container.innerHTML = '';
    }
  }

  // --- Errore ---

  private mostraErrore(messaggio: string): void {
    const container = this.$('[data-errore]');
    if (container) {
      container.innerHTML = `<div class="alert alert-danger alert-sm py-1 px-2">${messaggio}</div>`;
    }
  }

  // --- Reset bottone submit ---

  private resetSubmitBtn(): void {
    const btn = this.$('[data-submit-btn]') as HTMLButtonElement | null;
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<i class="bi bi-check-circle me-1"></i>Verifica e salva';
    }
  }

  // --- Chiusura modale / notifica al contesto parent ---

  private notifyAndClose(detail: { type: string; id: number }): void {
    if (window !== window.parent) {
      window.parent.postMessage({ action: 'modal-close', refresh: true, detail }, window.location.origin);
    } else {
      const modalEl = this.closest('.modal');
      if (modalEl) {
        const bsModal = (globalThis as any).bootstrap?.Modal?.getInstance(modalEl);
        if (bsModal) bsModal.hide();
      }
      this.dispatchEvent(new CustomEvent('disdetta-saved', { bubbles: true, composed: true, detail }));
    }
  }
}
